import { Grid, Typography } from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import {
  ArrayInput,
  BooleanInput,
  Button,
  SimpleFormIterator,
  TextInput,
  required,
  useNotify,
  useRecordContext,
  useSimpleFormIteratorItem,
} from "react-admin";
import { useWatch } from "react-hook-form";
import FormModal from "server/components/Modals/FormModal";
import EmployeeInput from "server/components/Fields/EmployeeInput";
import dataProvider from "server/dataProvider";
import { doctoralRequests } from "api/doctoralApi";
import { employeeRequests } from "api/employeeApi";

const JuryMemberFields = ({ sourcePrefix = "", watchPrefix }) => {
  const src = name => (sourcePrefix ? `${sourcePrefix}.${name}` : name);
  const isExternal = useWatch({ name: `${watchPrefix}.isExternal` });

  return (
    <Grid
      container
      spacing={2}
      sx={{ width: "100%" }}
    >
      <Grid
        item
        xs={12}
        md={3}
      >
        <BooleanInput
          source={src("isExternal")}
          label="Външен член"
        />
      </Grid>
      {isExternal ? (
        <>
          <Grid
            item
            xs={12}
            md={3}
          >
            <TextInput
              source={src("fullName")}
              label="Име"
              validate={required("Задължително")}
              fullWidth
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={3}
          >
            <TextInput
              source={src("university")}
              label="Университет"
              validate={required("Задължително")}
              fullWidth
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={3}
          >
            <TextInput
              source={src("scientificDegree")}
              label="Научна степен"
              fullWidth
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={3}
          >
            <TextInput
              source={src("email")}
              label="Имейл"
              fullWidth
            />
          </Grid>
        </>
      ) : (
        <Grid
          item
          xs={12}
          md={9}
        >
          <EmployeeInput
            source={src("employeeId")}
            label="Член (служител)"
            reference={employeeRequests.getEmployeesWithDegreeSelect()}
            validate={required("Задължително")}
            fullWidth
          />
        </Grid>
      )}
    </Grid>
  );
};

const OtherJuryMemberRow = () => {
  const { index } = useSimpleFormIteratorItem();
  return <JuryMemberFields watchPrefix={`members.${index}`} />;
};

const emptyMember = {
  employeeId: null,
  fullName: "",
  university: "",
  scientificDegree: "",
  email: "",
  isExternal: false,
};

const toFields = member => ({
  employeeId: member?.employeeId ?? null,
  fullName: member?.fullName ?? "",
  university: member?.university ?? "",
  scientificDegree: member?.scientificDegree ?? "",
  email: member?.email ?? "",
  isExternal: member ? !member.employeeId : false,
});

const toDefaultValues = defense => {
  const members = defense?.juryMembers ?? [];
  const chairman = members.find(m => m.isChairman);
  const reviewer = members.find(m => m.isReviewer && m !== chairman);
  const others = members.filter(m => m !== chairman && m !== reviewer);

  return {
    chairman: chairman ? toFields(chairman) : { ...emptyMember },
    reviewer: reviewer ? toFields(reviewer) : { ...emptyMember },
    members: others.map(toFields),
  };
};

const buildMember = (member, { isChairman, isReviewer }) => ({
  employeeId: member.isExternal ? null : member.employeeId,
  fullName: member.isExternal ? member.fullName : null,
  university: member.isExternal ? member.university : null,
  scientificDegree: member.isExternal ? member.scientificDegree : null,
  email: member.isExternal ? member.email : null,
  isChairman,
  isReviewer,
});

const DefenseJuryDialog = ({ defense, startNewAttempt = false, onSaved }) => {
  const record = useRecordContext();
  const notify = useNotify();

  if (!record?.id) return null;

  const triggerLabel = startNewAttempt
    ? "Създай ново жури"
    : defense?.juryMembers?.length
      ? "Редактирай журито"
      : "Създай жури";

  const handleConfirm = async data => {
    const payload = {
      juryMembers: [
        buildMember(data.chairman, { isChairman: true, isReviewer: false }),
        buildMember(data.reviewer, { isChairman: false, isReviewer: true }),
        ...(data.members ?? []).map(m => buildMember(m, { isChairman: false, isReviewer: false })),
      ],
    };
    await dataProvider.customRequest(doctoralRequests.saveDoctorantDefenseJury(record.id), payload, "PUT");
    notify("Журито е запазено успешно", { type: "success" });
    onSaved?.();
  };

  return (
    <FormModal
      titleContent="Изпитно жури"
      saveTitle="Запази"
      dialogSize="md"
      defaultValues={toDefaultValues(defense)}
      onConfirm={handleConfirm}
      triggerButton={
        <Button
          label={triggerLabel}
          variant="contained"
          startIcon={<GroupsIcon />}
        />
      }
    >
      <Typography
        color="primary"
        sx={{ marginTop: "1rem" }}
      >
        Председател
      </Typography>
      <JuryMemberFields
        sourcePrefix="chairman"
        watchPrefix="chairman"
      />

      <Typography
        color="primary"
        sx={{ marginTop: "1.5rem" }}
      >
        Рецензент
      </Typography>
      <JuryMemberFields
        sourcePrefix="reviewer"
        watchPrefix="reviewer"
      />

      <Typography
        color="primary"
        sx={{ marginTop: "1.5rem" }}
      >
        Други членове
      </Typography>
      <ArrayInput
        source="members"
        label={false}
      >
        <SimpleFormIterator
          inline={false}
          disableClear
        >
          <OtherJuryMemberRow />
        </SimpleFormIterator>
      </ArrayInput>
    </FormModal>
  );
};

export default DefenseJuryDialog;
